<template>
  <div
    ref="mapContainer"
    :style="{ width: '100%', height: height }"
  ></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";

interface Props {
  // 经度
  longitude?: number;
  // 纬度
  latitude?: number;
  // 地址（用于地理编码）
  address?: string;
  // 地图缩放级别
  zoom?: number;
  // 地图高度
  height?: string;
  // 是否显示标记
  showMarker?: boolean;
  // 标记标题
  markerTitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  longitude: 121.404,
  latitude: 31.081,
  address: "上海市闵行区万芳路515号",
  zoom: 15,
  height: "400px",
  showMarker: true,
  markerTitle: "易目秀信息科技(上海)有限公司",
});

const mapContainer = ref<HTMLElement | null>(null);
let map: any = null;
let marker: any = null;
let textLabel: any = null;
let geocoder: any = null;

// 初始化地图
const initMap = () => {
  if (!mapContainer.value || !(window as any).AMap) {
    console.error("地图容器不存在或高德地图API未加载");
    console.log("mapContainer.value:", mapContainer.value);
    console.log("AMap:", (window as any).AMap);
    return;
  }

  try {
    // 创建地图实例
    map = new (window as any).AMap.Map(mapContainer.value, {
      zoom: props.zoom,
      center: [props.longitude, props.latitude],
      viewMode: "3D", // 3D视图
      mapStyle: "amap://styles/normal", // 地图样式
      logoPosition: "none", // 隐藏高德地图logo
    });

    console.log("地图实例创建成功");

    // 等待地图加载完成后再添加标记
    map.on("complete", () => {
      console.log("地图加载完成，开始添加标记");

      // 如果提供了地址，使用地理编码获取精确坐标
      if (props.address) {
        // 先加载 Geocoder 插件
        (window as any).AMap.plugin("AMap.Geocoder", () => {
          console.log("Geocoder 插件加载完成");

          geocoder = new (window as any).AMap.Geocoder({
            city: "上海市", // 城市设为上海市
          });

          geocoder.getLocation(props.address, (status: string, result: any) => {
            if (status === "complete" && result.geocodes.length) {
              const location = result.geocodes[0].location;
              const lng = location.lng;
              const lat = location.lat;

              console.log("地理编码成功，坐标:", lng, lat);

              // 更新地图中心
              map.setCenter([lng, lat]);

              // 创建标记
              if (props.showMarker) {
                createMarker(lng, lat);
              }
            } else {
              console.log("地理编码失败，使用默认坐标");
              // 如果地理编码失败，使用默认坐标
              if (props.showMarker) {
                createMarker(props.longitude, props.latitude);
              }
            }
          });
        });
      } else {
        // 直接使用坐标创建标记
        console.log("直接使用坐标创建标记:", props.longitude, props.latitude);
        if (props.showMarker) {
          createMarker(props.longitude, props.latitude);
        }
      }
    });
  } catch (error) {
    console.error("创建地图实例失败:", error);
    if (mapContainer.value) {
      mapContainer.value.innerHTML =
        '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #f00;">地图初始化失败: ' +
        (error as Error).message +
        "</div>";
    }
    return;
  }
};

// 创建标记
const createMarker = (lng: number, lat: number) => {
  if (!map) {
    console.error("地图实例不存在，无法创建标记");
    return;
  }

  // 如果已存在标记，先移除
  if (marker) {
    map.remove(marker);
    marker = null;
  }

  console.log("创建标记，位置:", lng, lat);

  try {
    // 创建自定义红色大头针图标（SVG）
    const createCustomRedPinIcon = () => {
      const svg = `
        <svg width="32" height="42" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 42">
          <defs>
            <linearGradient id="pinGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color:#FF3B3B;stop-opacity:1" />
              <stop offset="50%" style="stop-color:#FF1A1A;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#CC0000;stop-opacity:1" />
            </linearGradient>
            <filter id="shadow">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
            </filter>
          </defs>
          <!-- 大头针阴影 -->
          <ellipse cx="16" cy="38" rx="8" ry="3" fill="#000000" opacity="0.2"/>
          <!-- 大头针主体 -->
          <path d="M16 2 C10 2 5 7 5 13 C5 18 16 40 16 40 C16 40 27 18 27 13 C27 7 22 2 16 2 Z" 
                fill="url(#pinGradient)" 
                stroke="#AA0000" 
                stroke-width="1"
                filter="url(#shadow)"/>
          <!-- 高光效果 -->
          <ellipse cx="16" cy="12" rx="6" ry="8" fill="#FFFFFF" opacity="0.3"/>
          <!-- 白色圆点 -->
          <circle cx="16" cy="15" r="5" fill="#FFFFFF"/>
          <circle cx="16" cy="15" r="3" fill="#FF6B6B" opacity="0.5"/>
        </svg>
      `;
      return (
        "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)))
      );
    };

    // 创建自定义红色大头针样式的标记
    marker = new (window as any).AMap.Marker({
      position: new (window as any).AMap.LngLat(lng, lat), // 使用 LngLat 对象
      title: props.markerTitle,
      icon: new (window as any).AMap.Icon({
        size: new (window as any).AMap.Size(32, 42), // 自定义尺寸
        // 使用自定义SVG图标
        image: createCustomRedPinIcon(),
        imageOffset: new (window as any).AMap.Pixel(0, 0),
        imageSize: new (window as any).AMap.Size(32, 42),
      }),
      offset: new (window as any).AMap.Pixel(-16, -42), // 标记偏移，使底部对齐坐标点
    });

    // 将标记添加到地图
    map.add(marker);
    console.log("标记已添加到地图");

    // 创建地址文字标签，显示在大头针底部
    if (textLabel) {
      map.remove(textLabel);
      textLabel = null;
    }

    const addressText = props.address || "上海市闵行区万芳路515号";
    // 将地址分成两行显示（如果有"号"字，在"号"后换行）
    let displayText = addressText;
    if (addressText.includes("号") && addressText.length > 10) {
      const parts = addressText.split("号");
      displayText = parts[0] + "号" + (parts[1] ? "\n" + parts[1] : "");
    }

    textLabel = new (window as any).AMap.Text({
      text: displayText,
      position: new (window as any).AMap.LngLat(lng, lat),
      offset: new (window as any).AMap.Pixel(0, 20), // 标记下方显示文字
      style: {
        padding: "0",
        background: "transparent",
        "background-color": "transparent",
        border: "none",
        "border-radius": "0",
        "font-weight": "bold",
        "font-size": "14px",
        color: "#000000",
        "white-space": "pre-line",
        "text-align": "center",
        "line-height": "1.4",
        "box-shadow": "none",
        "font-family": "Noto Sans SC",
      },
    });

    map.add(textLabel);
    console.log("地址文字标签已添加");

    // 添加信息窗体（可选）
    const infoWindow = new (window as any).AMap.InfoWindow({
      content: `<div style="padding: 10px; min-width: 200px;">
        <div style="font-weight: bold; margin-bottom: 5px; font-size: 14px;">${props.markerTitle}</div>
        <div style="color: #666; font-size: 12px;">${addressText}</div>
      </div>`,
      offset: new (window as any).AMap.Pixel(0, -30),
    });

    // 点击标记显示信息窗体
    marker.on("click", () => {
      infoWindow.open(map, marker.getPosition());
    });

    // 确保标记和文字都可见
    map.setFitView([marker, textLabel], false, [50, 50, 50, 50], 16);
  } catch (error) {
    console.error("创建标记失败:", error);
  }
};

onMounted(() => {
  // 确保容器有高度和宽度
  if (mapContainer.value) {
    mapContainer.value.style.width = "100%";
    mapContainer.value.style.height = props.height || "400px";
    console.log("地图容器尺寸:", {
      width: mapContainer.value.offsetWidth,
      height: mapContainer.value.offsetHeight,
      styleHeight: props.height,
    });
  }

  // 初始化地图的函数
  const tryInitMap = () => {
    if ((window as any).AMap && mapContainer.value) {
      console.log("高德地图API已加载，开始初始化地图");
      console.log("地图容器:", mapContainer.value);
      initMap();
      return true;
    }
    return false;
  };

  // 立即尝试初始化
  if (tryInitMap()) {
    return;
  }

  // 如果API未加载，等待加载完成
  let checkCount = 0;
  const maxChecks = 100; // 最多检查10秒 (100 * 100ms)

  const checkAMap = setInterval(() => {
    checkCount++;
    if (tryInitMap()) {
      clearInterval(checkAMap);
    } else if (checkCount >= maxChecks) {
      clearInterval(checkAMap);
      console.error("高德地图API加载超时，请检查：");
      console.error("1. API Key是否正确");
      console.error("2. 域名是否在白名单中");
      console.error("3. 网络连接是否正常");
      if (mapContainer.value) {
        mapContainer.value.innerHTML =
          '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;">地图加载失败，请检查控制台错误信息</div>';
      }
    }
  }, 100);
});

onBeforeUnmount(() => {
  // 销毁地图实例
  if (map) {
    if (marker) {
      map.remove(marker);
    }
    if (textLabel) {
      map.remove(textLabel);
    }
    map.destroy();
    map = null;
  }
  if (marker) {
    marker = null;
  }
  if (textLabel) {
    textLabel = null;
  }
  if (geocoder) {
    geocoder = null;
  }
});
</script>

<style scoped>
div {
  width: 100%;
  height: 100%;
}

/* 隐藏高德地图版权信息 */
:deep(.amap-copyright) {
  display: none !important;
}

:deep(.amap-logo) {
  display: none !important;
}
</style>
